import {
  Card,
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useMemo, useState, useEffect, useRef } from "react";

import { useDispatch } from "react-redux";
import {
  showLoader,
  dismissLoader,
  showSuccessMessage,
  showErroMessage,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
 import WdDivider from "../../../../Components/WdDivider";

const initialValues = {
  customerId: "",
  serviceGroupId: "",
  serviceId: "",
  description: "",
};

const validationSchema = object({
  customerId: string().required("Customer Organisation is required"),
  serviceId: string().required("Service is required"),
  description: string(),
});

const ReplyForm = () => {
  const [services, setServices] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [chargeMapping, setChargeMapping] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isServiceEnabled, setIsServiceEnabled] = useState(false);
  const [isFileUploadEnabled, setIsFileUploadEnabled] = useState(false);
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [serviceMessage, setServiceMessage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [detectedService, setDetectedService] = useState(null);
  const [selectedServiceGroupId, setSelectedServiceGroupId] = useState("");
  const formikRef = useRef();
  const pendingSubmitRef = useRef(null);
  const fileInputRef = useRef();
  const dispatch = useDispatch();

  const sortedOrganisations = useMemo(() => {
    if (!Array.isArray(organisations)) return [];
    return [...organisations].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
  }, [organisations]);

  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
    };

    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  // Fetch customer organisations on mount
  useEffect(() => {
    dispatch(showLoader());
    const params = { type: "CUSTOMER" };
    AppService.get("v1/organisation/keyValues", { params })
      .then((response) => {
        setOrganisations(response);
      })
      .catch((error) => {
        console.error("Error fetching organisations:", error);
        dispatch(showErroMessage("Failed to load customer organisations"));
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  }, [dispatch]);

  // Set screen title
  useEffect(() => {
    dispatch(updateScreenTitle("Reply Form"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Fetch service groups on mount
  useEffect(() => {
    AppService.get("v1/serviceGroup")
      .then((response) => {
        setServiceGroups(response || []);
      })
      .catch((error) => {
        console.error("Error fetching service groups:", error);
      });
  }, []);

  useEffect(() => {
    if (!detectedService?.serviceName) return;
    if (!services.length) return;
    if (!formikRef.current?.values) return;

    const confidence = parseFloat(detectedService.serviceConfidence) || 0;
    if (confidence <= 90) return;

    if (formikRef.current.values.serviceId) return;

    const matchedService = findMatchingService(detectedService.serviceName);
    if (!matchedService) return;

    formikRef.current.setFieldValue("serviceId", matchedService.id);
    setChargeMapping(matchedService);
    setServiceMessage({
      type: "success",
      text: `Service "${matchedService.name}" auto-selected (${confidence}% confidence)`,
    });
  }, [services, detectedService]);

  // Handle organisation change - fetch mapped services
  const handleOrganisationChange = (event, setFieldValue) => {
    const orgId = event.target.value;
    setFieldValue("customerId", orgId);
    setFieldValue("serviceGroupId", ""); // Reset service group selection
    setFieldValue("serviceId", ""); // Reset service selection
    setServices([]); // Clear services
    setChargeMapping(undefined);
    setIsServiceEnabled(false); // Disable service selection until document is processed
    setIsFileUploadEnabled(false); // Disable file upload until service group is selected
    setServiceMessage(null); // Clear any previous message
    setUploadedFiles([]); // Clear uploaded files
    setExtractedData(null); // Clear extracted data
    setDetectedService(null);
    setSelectedServiceGroupId(""); // Clear selected service group

    if (orgId) {
      dispatch(showLoader());
      AppService.get(`v1/serviceMapping/mappedServices/${orgId}`)
        .then((response) => {
          setServices(response);
        })
        .catch((error) => {
          console.error("Error fetching services:", error);
          dispatch(showErroMessage("Failed to load services"));
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  // Handle service group change
  const handleServiceGroupChange = (event, setFieldValue) => {
    const groupId = event.target.value;
    setFieldValue("serviceGroupId", groupId);
    setFieldValue("serviceId", ""); // Reset service selection
    setSelectedServiceGroupId(groupId);
    setChargeMapping(undefined);
    setIsServiceEnabled(false); // Disable service selection until document is processed
    setServiceMessage(null); // Clear any previous message
    setUploadedFiles([]); // Clear uploaded files
    setExtractedData(null); // Clear extracted data
    setDetectedService(null);
    
    if (groupId) {
      setIsFileUploadEnabled(true); // Enable file upload after service group is selected
    } else {
      setIsFileUploadEnabled(false);
    }
  };

  // Exact match service name from API response to service list (case-insensitive only)
  const findMatchingService = (serviceName) => {
    if (!serviceName || !services.length) return null;
    
    const normalizedApiName = serviceName.toLowerCase().trim();
    
    // Try exact match on name (case-insensitive only)
    let match = services.find(s => s.name.toLowerCase().trim() === normalizedApiName);
    if (match) return match;
    
    // Try exact match on description (case-insensitive only)
    match = services.find(s => 
      s.description && s.description.toLowerCase().trim() === normalizedApiName
    );
    if (match) return match;
    
    return null;
  };

  // Process documents and extract service info with retry logic
  const processDocumentsForService = async (files, setFieldValue, customerOrgId) => {
    if (!selectedServiceGroupId) {
      setServiceMessage({ type: 'error', text: 'Please select a Service Group before extracting.' });
      return;
    }
    if (!Array.isArray(files) || files.length === 0) {
      setServiceMessage({ type: 'error', text: 'Please upload at least one document to extract.' });
      return;
    }

    setIsExtracting(true);
    setIsProcessingDocument(true);
    setServiceMessage(null);
    setExtractedData(null);
    setDetectedService(null);

    // Retry logic with 3 attempts - only for extraction/API failures
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000;
    let lastError = null;
    let successPayload = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`Extraction attempt ${attempt} of ${MAX_RETRIES}`);

        const formData = new FormData();
        files.forEach((f) => {
          if (f) formData.append('files', f);
        });

        const response = await AppService.post(
          'v1/documents/processing/application-form/extract',
          formData,
          {
            params: {
              serviceGroupId: selectedServiceGroupId,
              ...(customerOrgId ? { organisationId: customerOrgId } : {}),
            },
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          }
        );

        const payload = response?.data ?? response;

        // Check if response indicates an error (even with 200 status)
        if (payload && payload.code && payload.code.includes('ERROR')) {
          throw new Error(payload.message || 'Processing error');
        }

        successPayload = payload;
        break;
      } catch (err) {
        lastError = err;
        console.warn(`Extraction attempt ${attempt} failed:`, err.message || err);

        if (attempt < MAX_RETRIES) {
          console.log(`Retrying in ${RETRY_DELAY}ms...`);
          await new Promise((res) => setTimeout(res, RETRY_DELAY));
        }
      }
    }

    if (successPayload) {
      if (successPayload && typeof successPayload === 'object') {
        setExtractedData(successPayload);
      } else {
        setExtractedData(null);
      }

      const { serviceName, serviceConfidence } = successPayload || {};
      const confidence = parseFloat(serviceConfidence) || 0;

      setDetectedService({ serviceName, serviceConfidence });

      if (confidence > 90) {
        const matchedService = findMatchingService(serviceName);
        if (matchedService) {
          setFieldValue('serviceId', matchedService.id);
          setChargeMapping(matchedService);
          setServiceMessage({
            type: 'success',
            text: `Service "${matchedService.name}" auto-selected (${confidence}% confidence)`,
          });
        } else {
          setServiceMessage({
            type: 'warning',
            text: `Service "${serviceName}" not found in list, please select manually.`,
          });
        }
      } else {
        setServiceMessage({
          type: 'info',
          text: `Detected service "${serviceName}" with low confidence (${confidence}%). Please select the service manually.`,
        });
      }
    } else {
      console.error('Error processing document after 3 attempts:', lastError);
      setServiceMessage({
        type: 'error',
        text: `Failed to process document after 3 attempts: ${lastError?.message || 'Unknown error'}. Please select the service manually.`,
      });
    }

    setIsProcessingDocument(false);
    setIsServiceEnabled(true);
    setIsExtracting(false);
  };

  const addFiles = (files, setFieldValue) => {
    if (!files || files.length === 0) return;

    const newFiles = files.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      id: Date.now() + Math.random(),
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  // Handle file selection
  const handleFileSelect = (event, setFieldValue) => {
    const files = Array.from(event.target.files || []);
    addFiles(files, setFieldValue);
    // Reset input so same file can be selected again
    event.target.value = "";
  };

  const handleDrop = (event, setFieldValue) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isFileUploadEnabled) return;

    const files = Array.from(event.dataTransfer?.files || []);
    addFiles(files, setFieldValue);
  };

  // Handle file removal
  const handleFileRemove = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatExtractedLabel = (key) => {
    if (!key) return "--";
    return String(key)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getDisplayExtractedEntries = (data) => {
    if (!data || typeof data !== "object") return [];

    const isEmpty = (v) =>
      v === null ||
      v === undefined ||
      (typeof v === "string" && v.trim() === "");

    return Object.entries(data)
      .filter(([key, value]) =>
        !["serviceName", "serviceConfidence"].includes(key) && !isEmpty(value)
      )
      .sort(([a], [b]) => a.localeCompare(b));
  };

  // Handle form submission
  const submitReplyForm = async (values, resetForm) => {
    if (isSubmitting) {
      console.warn("Submission already in progress");
      return;
    }

    if (uploadedFiles.length === 0) {
      dispatch(showErroMessage("Please upload at least one document"));
      return;
    }

    setIsSubmitting(true);
    dispatch(showLoader());

    try {
      const formData = new FormData();
      
      // Append all documents
      uploadedFiles.forEach((f) => {
        if (f?.file) formData.append('files', f.file);
      });
      
      // Append the data as JSON Blob with correct content type for @RequestPart
      const data = {
        customerOrgId: values.customerId,
        serviceId: values.serviceId,
        description: values.description,
        extractedData: extractedData || {},
      };
      const jsonBlob = new Blob([JSON.stringify(data)], { type: "application/json" });
      formData.append("data", jsonBlob);

      await AppService.post("v2/ticket/reply-form", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      resetForm();
      setUploadedFiles([]);
      setServices([]);
      setIsServiceEnabled(false);
      setIsFileUploadEnabled(false);
      setServiceMessage(null);
      setExtractedData(null);
      setDetectedService(null);
      setSelectedServiceGroupId("");
      dispatch(showSuccessMessage("Reply form submitted successfully!"));
    } catch (error) {
      console.error("Error submitting reply form:", error);
      dispatch(showErroMessage(error?.response?.data?.message || "Failed to submit reply form"));
    } finally {
      setIsSubmitting(false);
      dispatch(dismissLoader());
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (isSubmitting) {
      console.warn("Submission already in progress");
      return;
    }

    if (uploadedFiles.length === 0) {
      dispatch(showErroMessage("Please upload at least one document"));
      return;
    }

    pendingSubmitRef.current = { values, resetForm };
    setIsConfirmOpen(true);
  };

  return (
    <WDBox py={1}>
      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({ errors, isValid, touched, dirty, setFieldValue, setFieldTouched, values }) => (
            <Form>
              <Dialog
                open={isConfirmOpen}
                onClose={() => {
                  setIsConfirmOpen(false);
                  pendingSubmitRef.current = null;
                }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Confirm before submitting</DialogTitle>
                <DialogContent dividers>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Please confirm the selected customer organisation and service.
                  </Alert>

                  <Stack spacing={1.5}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderColor: "warning.main", backgroundColor: "rgba(237, 108, 2, 0.06)" }}
                    >
                      <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                        Customer Organisation
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: "break-word" }}>
                        {sortedOrganisations.find((o) => String(o?.id) === String(values.customerId))?.name || "--"}
                      </Typography>
                    </Paper>

                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderColor: "warning.main", backgroundColor: "rgba(237, 108, 2, 0.06)" }}
                    >
                      <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                        Service
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: "break-word" }}>
                        {services.find((s) => String(s?.id) === String(values.serviceId))?.name || "--"}
                      </Typography>
                    </Paper>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                  <WDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsConfirmOpen(false);
                      pendingSubmitRef.current = null;
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </WDButton>
                  <WDButton
                    variant="gradient"
                    color="info"
                    onClick={async () => {
                      const pending = pendingSubmitRef.current;
                      setIsConfirmOpen(false);
                      pendingSubmitRef.current = null;
                      if (!pending) return;
                      await submitReplyForm(pending.values, pending.resetForm);
                    }}
                    disabled={isSubmitting}
                  >
                    Proceed
                  </WDButton>
                </DialogActions>
              </Dialog>

              <WDBox>
                <WDTypography fontSize={14} fontWeight="medium">
                  Reply Form Details
                </WDTypography>
              </WDBox>

              <WDBox my={3}>
                <Grid container spacing={3}>
                  {/* Customer Organisation Selection */}
                  <Grid item xs={12}>
                    <Autocomplete
                      options={sortedOrganisations}
                      value={sortedOrganisations.find((o) => o?.id === values.customerId) || null}
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onChange={(_, newValue) => {
                        const orgId = newValue?.id || '';
                        handleOrganisationChange({ target: { value: orgId } }, setFieldValue);
                      }}
                      filterOptions={(options, { inputValue }) => {
                        const q = (inputValue || '').trim().toLowerCase();
                        // If user hasn't typed anything, allow browsing full list
                        if (q.length === 0) {
                          return [...options].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
                        }

                        // Only start filtering after 2+ characters (match EmployeeCreate profession UX)
                        if (q.length < 2) {
                          return [];
                        }

                        return options
                          .filter((o) => (o?.name || '').toLowerCase().includes(q))
                          .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer Organisation"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(237, 108, 2, 0.04)",
                              "& fieldset": { borderColor: "warning.main" },
                              "&:hover fieldset": { borderColor: "warning.dark" },
                              "&.Mui-focused fieldset": { borderColor: "warning.main", borderWidth: 2 },
                            },
                          }}
                          error={Boolean(errors.customerId) && Boolean(touched.customerId)}
                          helperText={touched.customerId && errors.customerId ? errors.customerId : ''}
                          onBlur={(e) => {
                            params?.inputProps?.onBlur?.(e);
                            setFieldTouched('customerId', true, true);
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Service Group Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="service-group-label">Service Group</InputLabel>
                      <Field
                        name="serviceGroupId"
                        label="Service Group"
                        labelId="service-group-label"
                        size="small"
                        fullWidth
                        disabled={!values.customerId}
                        as={Select}
                        onChange={(e) => handleServiceGroupChange(e, setFieldValue)}
                      >
                        {serviceGroups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Field>
                      {!values.serviceGroupId && values.customerId && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                          Select a service group to enable file upload
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Service Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="service-label">Service</InputLabel>
                      <Field
                        name="serviceId"
                        label="Service"
                        labelId="service-label"
                        size="small"
                        fullWidth
                        disabled={!values.customerId || !isServiceEnabled}
                        as={Select}
                        sx={{
                          backgroundColor: "rgba(237, 108, 2, 0.04)",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "warning.dark" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
                        }}
                        onChange={(e) => {
                          setFieldValue("serviceId", e.target.value);
                          const selectedService = services.find(
                            (service) => service && String(service.id) === String(e.target.value)
                          );
                          setChargeMapping(selectedService);
                        }}
                        error={Boolean(errors.serviceId) && Boolean(touched.serviceId)}
                      >
                        {services.map((service) => (
                          <MenuItem key={service.id} value={service.id}>
                            {service.name}
                          </MenuItem>
                        ))}
                      </Field>
                      {!isServiceEnabled && values.customerId && values.serviceGroupId && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                          Upload a document to enable service selection
                        </Typography>
                      )}
                      {isProcessingDocument && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="textSecondary">
                            Processing document...
                          </Typography>
                        </Box>
                      )}
                      {serviceMessage && (
                        <Alert severity={serviceMessage.type} sx={{ mt: 1, py: 0 }}>
                          {serviceMessage.text}
                        </Alert>
                      )}
                      {touched.serviceId && errors.serviceId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.serviceId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {getDisplayExtractedEntries(extractedData).length > 0 && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid #e6e8eb",
                          backgroundColor: "#fafbfc",
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Extracted details
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Auto-filled from uploaded document
                          </Typography>
                        </Box>

                        <Grid container spacing={1.5}>
                          {getDisplayExtractedEntries(extractedData).map(([key, value]) => (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ display: "block", lineHeight: 1.2, mb: 0.25 }}
                              >
                                {formatExtractedLabel(key)}
                              </Typography>
                              <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
                                {String(value)}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  {/* Ticket Description */}
                  <Grid item xs={12}>
                    <Field
                      name="description"
                      label="Ticket Description"
                      fullWidth
                      as={TextField}
                      size="small"
                      multiline
                      rows={4}
                      error={Boolean(errors.description) && Boolean(touched.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>
                </Grid>
              </WDBox>

              {/* Document Upload Section */}
              <WDBox my={3}>
                <WDTypography fontSize={14} fontWeight="medium" mb={2}>
                  Upload Documents
                </WDTypography>

                {/* Upload Button */}
                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    cursor: isFileUploadEnabled ? "pointer" : "not-allowed",
                    transition: "all 0.3s ease",
                    opacity: isFileUploadEnabled ? 1 : 0.5,
                    "&:hover": isFileUploadEnabled ? {
                      borderColor: "#1a8a7e",
                      backgroundColor: "rgba(26, 138, 126, 0.04)",
                    } : {},
                  }}
                  onClick={() => isFileUploadEnabled && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => handleDrop(e, setFieldValue)}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileSelect(e, setFieldValue)}
                    style={{ display: "none" }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    multiple
                  />
                  <CloudUploadIcon sx={{ fontSize: 48, color: "#1a8a7e", mb: 1 }} />
                  <Typography variant="body1" color="textSecondary">
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    PDF, DOC, DOCX, JPG, PNG (Max 10MB each)
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <WDButton
                    variant="gradient"
                    color="info"
                    disabled={
                      !isFileUploadEnabled ||
                      uploadedFiles.length === 0 ||
                      isProcessingDocument ||
                      isExtracting
                    }
                    onClick={() =>
                      processDocumentsForService(
                        uploadedFiles.map((f) => f.file).filter(Boolean),
                        setFieldValue,
                        values.customerId
                      )
                    }
                  >
                    {isExtracting ? 'Extracting...' : 'Extract'}
                  </WDButton>
                </Box>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Uploaded Files ({uploadedFiles.length})
                    </Typography>
                    {uploadedFiles.map((file) => (
                      <Paper
                        key={file.id}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <InsertDriveFileIcon sx={{ color: "#1a8a7e" }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleFileRemove(file.id)}
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                )}
              </WDBox>

              {chargeMapping && (
                <WDBox my={6}>
                  <WdDivider>
                    <Typography fontSize={14}>Charges</Typography>
                  </WdDivider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <WDTypography variant="caption">Govt.Charge</WDTypography>
                        <WDTypography variant="h6" fontSize={14}>
                          {chargeMapping.govtCharge}
                        </WDTypography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <WDTypography variant="caption">Service Charge</WDTypography>
                        <WDTypography variant="h6" fontSize={14}>
                          {chargeMapping.serviceCharge}
                        </WDTypography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <WDTypography variant="caption">Tax On Service Charge</WDTypography>
                        <WDTypography variant="h6" fontSize={14}>
                          {chargeMapping.taxOnServiceCharge}
                        </WDTypography>
                      </Box>
                    </Grid>
                  </Grid>
                </WDBox>
              )}

              {/* Submit Button */}
              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton
                  variant="gradient"
                  disabled={!values.customerId || !values.serviceId || isSubmitting || uploadedFiles.length === 0}
                  type="submit"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default ReplyForm;
